import ErrorBoundary from '@components/ErrorBoundary';
import PageContainer from '@components/PageContainer';
import { OverallAnalysis, OverallAnalysisNotFound } from '@components/common/OverallAnalysis';
import StructuredReport from '@components/common/StructuredReport';
import NoAnalysis from '@components/dynamic-survey/details/NoAnalysis';
import Spinner from '@components/spinner/Spinner';
import { useSurveyOverallAnalysis } from '@hooks/getQueries';
import { Box } from '@mui/material';
import { Suspense } from 'react';
import { useParams } from 'react-router-dom';

const OverallAnalysisContainer = () => {
  const { id } = useParams();
  const { data } = useSurveyOverallAnalysis({ surveyId: id });

  // Prefer the Claude-authored structured report when the analysis-agent
  // one-shot task produced one (structured_report_status === "completed").
  // Fall back to the older overall_analysis narrative in every other case
  // — agent not configured for this env, task failed, task returned an
  // empty body, chart uploads faulted. That fallback path is exactly the
  // rendering the tab used before this feature landed, so a customer
  // whose survey didn't clear the agent path still sees a report.
  const structuredReport = data?.structured_report;
  const hasStructuredReport = Boolean(structuredReport?.markdown?.trim());

  return (
    <Box sx={{ padding: '10px', my: '20px', width: '688px', mx: 'auto' }}>
      {hasStructuredReport ? (
        <StructuredReport report={structuredReport} />
      ) : data?.overall_analysis ? (
        <OverallAnalysis data={data?.overall_analysis?.report_response} />
      ) : (
        <OverallAnalysisNotFound text="Survey" />
      )}
    </Box>
  );
};

export default function SurveyOverallAnalysis() {
  const { id } = useParams();

  return (
    <PageContainer title={'Survey | Overall Analysis'} description={'Survey'}>
      <Suspense fallback={<Spinner />}>
        <ErrorBoundary FallbackComponent={() => <NoAnalysis studyId={id} />}>
          <Suspense fallback={<Spinner />}>
            <OverallAnalysisContainer />
          </Suspense>
        </ErrorBoundary>
      </Suspense>
    </PageContainer>
  );
}
